(ns kyber.events.bootstrap
  (:require [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [goog.object :as gobj]
            [re-frame.core :as re-frame]
            [applied-science.js-interop :as j]
            [kyber.utils :as ku]
            [kyber.constants :as c]
            [kyber.lib.error-reporting :as ke]
            ["logrocket" :as LogRocket]))

(defn matches-polyfill
  []
  (let [ElementProto (-> js/window .-Element .-prototype)]
    (when-not (= (type (.-matches ElementProto)) js/Function)
      (set! (.-matches ElementProto)
            (or (.-msMatchesSelector ElementProto)
                (.-mozMatchesSelector ElementProto)
                (.-webkitMatchesSelector ElementProto))))))

(defn closest-polyfill
  []
  (let [ElementProto (-> js/window .-Element .-prototype)]
    (when-not (= (type (.-closest ElementProto)) js/Function)
      (set! (.-closest ElementProto)
            (fn [selector]
              (this-as this
                       (when (-> js/document .-documentElement (.contains this))
                         (loop [element this match nil]
                           (or
                            match
                            (if (.matches element selector)
                              (recur element element)
                              (let [parent-el (or (.-parentElement element)
                                                  (.-parentNode element))]
                                (when (and (some? parent-el)
                                           (= (.-nodeType parent-el) 1))
                                  (recur parent-el match)))))))))))))

(defn initialize-kyber-bootstrap
  "Registers Kyber bootstrap events & effects"
  []

  (re-frame/reg-fx
   :i18n/init
   #(re-frame/dispatch [:kyber.events.i18n/fetch-hardcoded-locale]))

  (re-frame/reg-fx
    :dev-tools-init
    (fn [_]
      (when ^boolean js/goog.DEBUG
        (enable-console-print!))))

  (re-frame/reg-fx
    :add-polyfils
    (fn [enabled]
      (when (true? enabled)
        (matches-polyfill)
        (closest-polyfill))))

  (re-frame/reg-fx
    :extend-cljs
    (fn [_]
      (extend-type js/FileList
        ISeqable
        (-seq [array] (array-seq array 0)))
      (extend-type js/NodeList
        ISeqable
        (-seq [array] (array-seq array 0)))
      (extend-type js/HTMLCollection
        ISeqable
        (-seq [array] (array-seq array 0)))))

  ;; Invokes whatever funciton is passed to it
  (re-frame/reg-fx
    :invoke
    (fn [fun]
      (when (some? fun)
        (fun))))

  ;; Dispatches an event in 'x' ms
  (re-frame/reg-fx
    :dispatch-in
    (fn [event]
      (.setTimeout
        js/window
        #(re-frame/dispatch (vec (rest event)))
        (first event))))

  (re-frame/reg-event-fx
    :dispatch-in
    (fn [{:keys [db]} event]
      {:db          db
       :dispatch-in (vec (rest event))}))

  (re-frame/reg-event-fx
    :dispatch-and-recur
    (fn [{:keys [db]} [_ {:keys [should-recur? delay event]
                          :as   event-payload}]]
      (cond-> {}
        (should-recur? db)
        (assoc :dispatch-n [event
                            [:dispatch-in delay
                             :dispatch-and-recur event-payload]]))))

  (re-frame/reg-fx
    :bootstrap/init-google-analytics
    (fn [should-init]
      (when (and (true? should-init)
                 (some? (seq c/google-tracking-id)))
        (ku/load-js (str "https://www.googletagmanager.com/gtag/js?"
                         c/google-tracking-id)
                    #(let [gtag (gobj/get js/window "gtag")]
                       (if (-> c/google-tracking-cross-domains first not-empty some?)
                         (gtag "config" c/google-tracking-id
                               (clj->js {"linker" {"domains" c/google-tracking-cross-domains}}))
                         (gtag "config" c/google-tracking-id)))))))

  (re-frame/reg-event-fx
    :bootstrap/spec-chec-polling
    (fn [{:keys [db]} [event-name _]]
      (let [error           (s/explain-data :app.db/db db)
            spec-error-uuid (str (random-uuid))]
        (cond-> {:db db
                 :dispatch-n []}
          (some? error)
          (update :dispatch-n conj
                  [:analytics/save-datapoint
                   {:event           event-name
                    :event-version   0
                    :spec-error-uuid spec-error-uuid}])

          (some? error)
          (update :dispatch-n conj
                  [:report-error
                   {:name     "Spec Error"
                    :tab-name "spec"
                    :payload  {:spec-error-uuid spec-error-uuid
                               :spec            (-> (update error
                                                            :cljs.spec.alpha/problems
                                                            (fn [problems]
                                                              (map #(select-keys % [:path])
                                                                   problems)))
                                                    (dissoc :cljs.spec.alpha/spec
                                                            :cljs.spec.alpha/value))}}])))))

  (re-frame/reg-fx
   :bootstrap/set-logrocket-user
   (fn [{:keys [sub email family-name given-name]}]
     (when (seq c/logrocket-app-id)
       (j/call LogRocket :identify email
               #js{:email       email
                   :user-id     sub
                   :family-name (or family-name "Unknown")
                   :given-name  (or given-name "Unknown")
                   :subdomain   (-> (gobj/getValueByKeys js/window
                                                         "location"
                                                         "hostname")
                                    (str/split #"\.")
                                    first)}))))

  (re-frame/reg-fx
   :bootstrap/init-logrocket
   (fn []
     (when (seq c/logrocket-app-id)
       (let [localhost? (-> js/window
                            .-location
                            .-hostname
                            (= "localhost"))]
         (when-not localhost?
           (j/call LogRocket :init c/logrocket-app-id #js{:release c/app-version}))))))

  (re-frame/reg-event-fx
    :bootstrap/init-bugsnag
    (fn [{:keys [db]} _]
      (let [bugsnag-api     (gobj/get js/window "bugsnag")
            bugsnag-enabled (and (some? (seq c/bugsnag-api-key))
                                 (some? bugsnag-api)
                                 (-> js/window
                                     .-location
                                     .-hostname
                                     (not= "localhost")))]
        {:db (assoc db
                    :bugsnag-client
                    (when bugsnag-enabled
                      (bugsnag-api
                        (clj->js (cond-> {:releaseStage c/app-env
                                          :apiKey c/bugsnag-api-key
                                          :notifyReleaseStages ["production"
                                                                "staging"
                                                                "development"]
                                          :beforeSend ke/before-send}
                                   (some? (seq c/app-version))
                                   (assoc :appVersion c/app-version))))))})))

  (re-frame/reg-event-fx
    :gdpr/acknowledge
    (fn [{:keys [db]} [event-name location]]
      {:db       db
       :dispatch [:analytics/save-datapoint
                  {:event            event-name
                   :event-version    0
                   :context-location location}]}))

  (re-frame/reg-fx
    :local-storage-set-item
    (fn [{:keys [key value]}]
      (ku/set-item key value)))

  (re-frame/reg-fx
    :scroll-window-to
    (fn [height]
      (set! (-> js/document .-body .-scrollTop) height)
      (set! (-> js/document .-documentElement .-scrollTop) height)))

  (re-frame/reg-fx
    :change-window-location
    (fn [url]
      (set! (.-location js/window)
            (if (some? url)
              url
              "/"))))

  (re-frame/reg-event-fx
    :report-error
    (fn [{:keys [db]} [_ error-payload]]
      {:db           db
       :report-error (assoc error-payload
                            :bugsnag-client
                            (:bugsnag-client db))}))

  (re-frame/reg-fx
    :report-error
    (fn [{:keys [bugsnag-client name tab-name payload]}]
      (if (some? bugsnag-client)
        (ke/notify bugsnag-client
                   (js/Error. name)
                   (cond-> {}
                     (some? payload)
                     (assoc-in [:metaData (or tab-name "unknown")] payload)))
        (do
          (js/console.error "No bugsnag client found to report error:")
          (js/console.error payload)))))

  (re-frame/reg-fx
    :bootstrap/spec-chec-polling
    (fn [should-start-polling]
      (when (true? should-start-polling)
        (js/setInterval
          #(re-frame/dispatch [:bootstrap/spec-chec-polling])
          c/spec-polling-interval))))

  (re-frame/reg-fx
    :bootstrap/print-app-version-information
    (fn [should-print]
      (when (true? should-print)
        (js/console.log "%cKyber Version Information"
                        (str "background: #000;"
                             "color: #fff; "
                             "padding: 4px; "
                             "font-weight: 700;"))
        (js/console.log (str "%cVersion: %c" c/app-version)
                        "font-weight: 700;"
                        "font-weight: 300;")
        (js/console.log (str "%cApp Name: %c" @c/app-name)
                        "font-weight: 700;"
                        "font-weight: 300;")
        (when js/goog.DEBUG
          (js/console.log (str "%cStack Name: %c" c/stack-name)
                          "font-weight: 700;"
                          "font-weight: 300;")))))

  (re-frame/reg-fx
    :bootstrap/setup-dev-hotkeys
    (fn []
      (when ^boolean js/goog.DEBUG
        (j/assoc! js/document :onkeyup
                  (fn [e]
                    (when (and (j/get e :shiftKey)
                               (j/get e :ctrlKey)
                               (= 84 (j/get e :which))) ;; t
                      (re-frame/dispatch [:ui/copy-id-token]))))))))
